import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./KpiCard.module.css";
import AnalyticsTooltip from "./AnalyticsTooltip";
import { DeltaInfo } from "../utils/computeDelta";
import { formatDecimal } from "../utils/formatNumber";

interface KpiCardProps {
    title: string;
    value: string;
    /** Secondary line under the main value (e.g. median under mean duration). */
    subValue?: string;
    icon?: React.ReactNode;
    /**
     * When true, the icon is rendered on its own row under the title (full
     * width). Use for wide content such as a 5-star strip — the default inline
     * layout keeps a 16×16 icon box which clips multi-star rows.
     */
    iconStack?: boolean;
    /** Shown when hovering the card (no info icon). */
    help?: string;
    delta?: DeltaInfo | null;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, subValue, icon, iconStack, help, delta }) => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string) => string;
    const comparisonLabel = tt("vsPreviousPeriod");

    const renderDelta = (): React.ReactNode => {
        if (!delta) return null;
        if (delta.sign === "flat") {
            return <p className={styles.delta}>{comparisonLabel}</p>;
        }
        const sign = delta.sign === "up" ? "+" : "−";
        const cls = delta.sign === "up" ? styles.deltaUp : styles.deltaDown;
        const suffix = delta.kind === "point" ? " pts" : "%";
        return (
            <p className={`${styles.delta} ${cls}`}>
                {sign}
                {formatDecimal(delta.value, 1)}
                {suffix} {comparisonLabel}
            </p>
        );
    };

    const card = (
        <div className={`${styles.card} ${help ? styles.hasTooltip : ""}`}>
            {iconStack && icon ? (
                <>
                    <div className={styles.header}>
                        <span className={styles.titleRow}>{title}</span>
                    </div>
                    <div className={styles.iconBand}>{icon}</div>
                </>
            ) : (
                <div className={styles.header}>
                    <span className={styles.titleRow}>
                        {icon ? <span className={styles.icon}>{icon}</span> : null}
                        {title}
                    </span>
                </div>
            )}
            <p className={styles.value}>{value}</p>
            {subValue ? <p className={styles.subValue}>{subValue}</p> : null}
            {renderDelta()}
        </div>
    );

    if (!help) return card;

    return <AnalyticsTooltip title={help}>{card}</AnalyticsTooltip>;
};

export default KpiCard;
